export type TurnStatus = "in-flight" | "live" | "accepted" | "rejected" | "aborted" | "failed";

export type TurnErrorKind = "rate-limit" | "schema" | "network" | "auth" | "aborted" | "unknown";

export interface TurnFieldProposal {
  fieldName: string;
  before: unknown;
  after: unknown;
  unchanged: boolean;
}

export interface TurnError {
  kind: TurnErrorKind;
  message?: string;
  retryAfter?: number;
}

export type TurnLanguageStatus = "pending" | "applied" | "rejected" | "failed";

export interface TurnLanguageProposal {
  languageId: number;
  translations: Record<string, unknown>;
  status: TurnLanguageStatus;
  failureError?: string;
}

export type TurnProposal =
  | {
      kind?: "generation";
      fields: TurnFieldProposal[];
      contextSummary: unknown;
    }
  | {
      kind: "propagation";
      sourceLanguageId: number;
      fieldNames: string[];
      languages: TurnLanguageProposal[];
    };

export interface Turn {
  id: string;
  createdAt: number;
  prompt: string;
  scope: string[];
  status: TurnStatus;
  proposal?: TurnProposal;
  error?: TurnError;
  appliedCount?: number;
  acceptedFieldNames?: string[];
}

export interface ThreadState {
  version: 1;
  turns: Turn[];
}

export const isPropagationProposal = (
  proposal: TurnProposal | undefined,
): proposal is Extract<TurnProposal, { kind: "propagation" }> =>
  !!proposal && (proposal as any).kind === "propagation";

export const isGenerationProposal = (
  proposal: TurnProposal | undefined,
): proposal is Extract<TurnProposal, { kind?: "generation" }> =>
  !!proposal && (proposal as any).kind !== "propagation";

const STORAGE_PREFIX = "slopify-thread:";
const MAX_TURNS = 20;
const MAX_BYTES = 512_000;

const keyFor = (contentId: number, languageId: number): string =>
  `${STORAGE_PREFIX}${contentId}:${languageId}`;

const emptyThread = (): ThreadState => ({ version: 1, turns: [] });

export const getThread = (contentId: number, languageId: number): ThreadState => {
  try {
    const raw = localStorage.getItem(keyFor(contentId, languageId));
    if (!raw) return emptyThread();
    const parsed = JSON.parse(raw) as ThreadState;
    if (!parsed || parsed.version !== 1 || !Array.isArray(parsed.turns)) {
      return emptyThread();
    }
    return parsed;
  } catch {
    return emptyThread();
  }
};

export const setThread = (contentId: number, languageId: number, state: ThreadState): void => {
  let turns = state.turns.slice(-MAX_TURNS);
  let serialized = JSON.stringify({ version: 1, turns });
  while (serialized.length > MAX_BYTES && turns.length > 1) {
    turns = turns.slice(1);
    serialized = JSON.stringify({ version: 1, turns });
  }
  if (serialized.length > MAX_BYTES) {

    console.warn("[slopifyThread] single turn exceeds byte cap, writing anyway");
  }
  try {
    localStorage.setItem(keyFor(contentId, languageId), serialized);
  } catch {
    /* ignore quota errors */
  }
};

export const clearThread = (contentId: number, languageId: number): void => {
  try {
    localStorage.removeItem(keyFor(contentId, languageId));
  } catch {
    /* ignore */
  }
};

export const clearAllThreads = (): void => {
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(STORAGE_PREFIX)) toRemove.push(key);
    }
    toRemove.forEach((k) => localStorage.removeItem(k));
  } catch {
    /* ignore */
  }
};

export const newTurnId = (): string => {
  try {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through */
  }
  return `t_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};
